/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Text, ActionButton } from '@components/atoms'
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	ButtonPropsColorOverrides,
} from '@mui/material'
import { OverridableStringUnion } from '@mui/types'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

export interface ModalInfoType {
	target: string
	title: string
	content?: JSX.Element
	value?: string
	onSubmit?: (value?: any) => void
}

interface PropsType {
	show: boolean

	target: string
	title: string
	customTitle?: JSX.Element
	showTitle?: boolean

	value?: string

	// close
	closeTitle?: string
	closeStatus?: OverridableStringUnion<
		'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
		ButtonPropsColorOverrides
	>
	closeIcon?: string
	closeIconColor?: string
	showClose?: boolean

	// submit
	submitTitle?: string
	submitStatus?: OverridableStringUnion<
		'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
		ButtonPropsColorOverrides
	>
	isSubmitDisabled?: boolean
	submitIcon?: string
	submitIconColor?: string
	showSubmit?: boolean
	isDisableDisimissionOnSubmit?: boolean

	// event
	onSubmit?: (value?: any) => void
	onCloseModal?: () => void

	// style
	isTitleDanger?: boolean
	style?: React.CSSProperties[] | React.CSSProperties
	rootStyle?: React.CSSProperties

	// contents
	children?: React.ReactNode
}

export type { PropsType as ModalTypes }

/**
 * 확인 모달 — surface 패널 · radius 0 · 헤어라인. 닫기는 텍스트, 확인은 고스트/블랙.
 * `*Status`·`*IconColor` 는 API 유지용으로 받기만 한다 — 위험한 동작도 색이 아니라 문장이 말한다.
 */
export const Modal = (props: PropsType) => {
	// state
	const [showModal, setShowModal] = React.useState<boolean>(props.show)

	// variables
	const showTitle: boolean = props.showTitle ?? true
	const showClose: boolean = props.showClose ?? true
	const showSubmit: boolean = props.showSubmit ?? true
	const isDisableDisimissionOnSubmit: boolean =
		props.isDisableDisimissionOnSubmit ?? false

	React.useEffect(() => {
		setShowModal(props.show)
	}, [props.show])

	const onCloseModal = () => {
		setShowModal(false)
		if (props.onCloseModal !== undefined) {
			props.onCloseModal()
		}
	}

	const onSubmit = () => {
		if (!isDisableDisimissionOnSubmit) {
			setShowModal(false)
		}

		if (!isDisableDisimissionOnSubmit && props.onCloseModal !== undefined) {
			props.onCloseModal()
		}
		if (props.onSubmit !== undefined) {
			props.onSubmit()
		}
	}

	return (
		<Dialog
			open={showModal}
			onClose={onCloseModal}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			css={[
				style,
				props.rootStyle !== undefined
					? { '.MuiPaper-elevation': { ...props.rootStyle } }
					: {},
			]}
		>
			{showTitle ? (
				<DialogTitle id="alert-dialog-title" className="modalTitle">
					{props.customTitle ?? (
						<Text className="dialogTitleText" as="span">
							{props.title}
						</Text>
					)}
				</DialogTitle>
			) : null}

			<DialogContent sx={{ ...props.style }} className="modalContentView">
				{props.children}
			</DialogContent>

			<DialogActions className="modalActionView">
				{showClose ? (
					<ActionButton
						title={props.closeTitle ?? '닫기'}
						tooltip={props.closeTitle ?? '닫기'}
						variant="text"
						onClick={onCloseModal}
						iconName={props.closeIcon ?? 'close'}
					/>
				) : null}

				{showSubmit && props.target !== 'duplicatedNameWithMovingAlert' ? (
					<ActionButton
						title={props.submitTitle ?? '확인'}
						tooltip={props.submitTitle ?? '확인'}
						variant="outlined"
						onClick={onSubmit}
						iconName={props.submitIcon ?? 'check'}
						disabled={props.isSubmitDisabled === true}
					/>
				) : null}
			</DialogActions>
		</Dialog>
	)
}

const style = css({
	fontFamily: boFont.ui,

	'.MuiPaper-root': {
		minWidth: '30rem',
		minHeight: '8rem',
		borderRadius: 0,
		background: mono.surface,
	},

	'.modalTitle': {
		display: 'flex',
		alignItems: 'center',
		padding: '20px 24px 14px',
		borderBottom: `1px solid ${mono.line}`,
		fontFamily: boFont.ui,

		'.dialogTitleText': {
			margin: 0,
			fontFamily: boFont.ui,
			fontSize: 16,
			fontWeight: 500,
			color: mono.ink,
		},
	},

	'.modalContentView': {
		minWidth: 300,
		padding: '20px 24px',
		overflowX: 'hidden',
		fontFamily: boFont.ui,
		fontSize: 13,
		fontWeight: 300,
		color: mono.dim,
		lineHeight: 1.6,

		'.text': { fontFamily: boFont.ui, fontWeight: 300, color: mono.dim, lineHeight: 1.6 },
	},

	'.modalActionView': {
		padding: '12px 24px 20px',
		borderTop: `1px solid ${mono.line}`,
		gap: 8,
	},

	'@media (max-width: 500px)': {
		'.MuiPaper-root': {
			minWidth: '100%',
			width: '100%',
			margin: 0,
		},

		'.modalContentView': {
			minWidth: '100%',
		},
	},
})
